import React, { ReactElement } from 'react';

import { getFilterDates, Route } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import FilterService from '@philips-emr/hpms-stipulator-commons/lib/services/filter-service';
import { HPMSOnRenderAction } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { Button, FetchAPI, FilterAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { ButtonTheme, Color, Placement } from '@philips-emr/tws-constants';
import Controller, { OnBeforeFetch, OnClear, OnDynamicContentRender, OnMount, OnSubmit, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { ClearEvent, FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HistoryService } from '@philips-emr/tws-core/lib/services';
import { applicationContext } from '@philips-emr/tws-foundation';

import { HistoricoAutorizacaoConsultaAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

enum IeStatus {
  AUTORIZADO = '1',
  EM_ANALISE = '2',
  NEGADO = '3',
}

interface Data {
  nrSeqGuia: number;
  dsStatus: string;
  ieStatus: IeStatus;
}

function getStamp(ieStatus: IeStatus, label: string): ReactElement | null {
  let stampProps = null;

  switch (ieStatus) {
    case IeStatus.AUTORIZADO:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case IeStatus.EM_ANALISE:
      stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label };
      break;
    case IeStatus.NEGADO:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
      break;
    default:
      return null;
  }

  return <Stamp {...stampProps} />;
}

@Controller({ fragment:FragmentNames.HISTORICO_AUTORIZACAO_CONSULTA })
export default class HistoricoAutorizacaoConsultaController extends BaseController {

  @OnMount(Aliases.PANEL_HISTORICO_AUTORIZACAO_CONSULTA)
  onMountContent() : void {
    applicationContext.openModule('historico-autorizacao');
  }

  @OnBeforeFetch(Aliases.FETCH_CONSULTA)
  public onBeforeFetchConsulta(event: FetchEvent): void {
    const api = event.target as unknown as FetchAPI;
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && api.setParameters({ ...api.getParameters(), nrSeqSubestipulante });
  }

  @OnSubmit(Aliases.FILTER_CONSULTA)
  public onSubmitFilter(event: SubmitEvent): void {
    const { record, target } = event;
    const api = target as unknown as FilterAPI;

    if (record && record.dtInicio && record.dtFim) {
      return;
    }

    const { dtInicio, dtFim } = getFilterDates('dtInicio', 'dtFim', 2);
    !record.dtInicio && (record.dtInicio = dtInicio);
    !record.dtFim && (record.dtFim = dtFim);

    api.setData({});
    api.setData({ ...api.getRecord(), dtInicio: record.dtInicio, dtFim: record.dtFim });
  }

  @HPMSOnRenderAction(Aliases.HPMS_CUSTOM_CARD)
  public onDynamicContentRenderVisualizacao(event: RenderEvent): void {
    const { render, data } = event;
    const { nrSeqGuia } = data as unknown as Data;

    render(() => (
      <Button
        key="report"
        icon="/static/assets/view-details.svg"
        theme={ButtonTheme.QUIET_EMPHASIS}
        value=""
        iconPlacement={Placement.LEFT}
        onClick={() => this.getHistorico(nrSeqGuia)}
      />
    ));
  }

  @OnClear(Aliases.FILTER_CONSULTA)
  public onClearFilter(event: ClearEvent): void {
    const api = event.target as unknown as FilterAPI;
    api.setData({});
    api.setData(getFilterDates());
  }

  @OnMount(Aliases.FILTER_CONSULTA)
  onFilterMount(event: MountEvent): void {
    FilterService.restoreFilterState(event);
  }

  @OnUnmount(Aliases.FILTER_CONSULTA)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_STAMP)
  public onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as Data;
    const stampExclusao = getStamp(ieStatus, dsStatus);

    if (stampExclusao) {
      render((): ReactElement => stampExclusao);
    }
  }

  private getHistorico(nrSeqGuia: number): void {
    HistoryService.push(Route.HISTORICO_AUTORIZACAO, { nrSeqGuia });
  }

}
